import type { MaybeRef } from 'vue'
import { computed, unref } from 'vue'
import type { RouteLocationNamedRaw, RouteLocationRaw, Router, RouterLinkProps } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'

export type PopupParams = RouteLocationNamedRaw
export type PushPopupFunction = (input: PopupParams) => ReturnType<Router["push"]>

export interface CongressRouter extends Router {
    pushPopup: PushPopupFunction
    replacePopup: PushPopupFunction
}

function _isNamedRoute(to: RouteLocationRaw): to is RouteLocationNamedRaw {
    return (typeof to !== 'string') && ('name' in to)
}

export function usePopupRouteData () {
    const router = useRouter()
    const route = useRoute()
    /**
     * Tweak the 'to' value such that:
     *
     * * path names starting with : are prepended with the name of the root route.
     *   :remote => control:remote
     *
     *
    fun ti*  extends RouerLinkPropst>PP
     */
    const parseProps = (props: MaybeRef<RouterLinkProps>): RouterLinkProps => {
        const _props = unref(props)
        const { to } = _props

        if (!to) {
            return _props
        }

        return {
            ..._props,
            to: parsePopup(to)!,
        }
    }

    function parsePopup<P extends RouteLocationRaw | undefined> (to: MaybeRef<P>): P {
        const _to = unref(to)
        const { matched } = route ?? {}

        if (_to === undefined) {
            return _to
        }

        if (typeof _to === 'string') {
            return _to
        }

        if (_isNamedRoute(_to)) {
            const { name, params, query } = _to

            const baseRoute = matched?.[0]
            const baseRouteName = String(baseRoute.name ?? '-')

            const isAbsolute = !String(name).startsWith(':')
            const childName = isAbsolute
                ? String(name)
                : `${baseRouteName}${String(name)}`

            const result = { name: childName, params, query } as P
            return result
        }

        return _to
    }

    const extendedRouter = computed(() =>
        router
         ? new Proxy(router, {
            get(target, prop, receiver) {
                if (prop === 'pushPopup') {
                    return ((input: PopupParams) => {
                        const to = parsePopup(input);
                        if (to) {
                            target.push(to)
                        } else {
                            console.error("could not determine the popup route", input)
                        }
                    }).bind(target)
                }

                if (prop === 'replacePopup') {
                    return ((input: PopupParams) => {
                        const to = parsePopup(input);
                        if (to) {
                            target.replace(to)
                        } else {
                            console.error("could not determine the popup route", input)
                        }
                    })
                }

                return Reflect.get(target, prop, receiver)
            }
        }) as CongressRouter
        : undefined
    )

    return {router: extendedRouter, parsePopup, parseProps, }
}
